import { useMutation, useQuery } from '@tanstack/react-query';

import { AiReplyApi, AiReplyRequestBody } from '@/apis/ai-replies';
import AppApi from '@/apis/app-api';
import { baseUrl } from '@/consts/route';
import { cleanArray } from '@/utils/cleanArray';
import { I18nLocale, localeToFull } from '@/utils/i18n';

import { useAuth } from './useAuth';

export const useAiReplyV1I18n = (locale: I18nLocale) => {
  const api = new AppApi(baseUrl);
  const aiReplyApi = new AiReplyApi(api);
  const fullLang = localeToFull(locale);

  return useMutation({
    mutationFn: (body: AiReplyRequestBody) =>
      aiReplyApi.postV1International({
        ...body,
        language: fullLang,
      }),
    mutationKey: ['ai-reply', 'v1'],
  });
};

export const useAiReplyV1 = () => {
  const api = new AppApi(baseUrl);
  const aiReplyApi = new AiReplyApi(api);

  return useMutation({
    mutationFn: (body: AiReplyRequestBody) => aiReplyApi.postV1(body),
    mutationKey: ['ai-reply', 'v1'],
  });
};

export const useAiReplyV2 = () => {
  const api = new AppApi(baseUrl);
  const aiReplyApi = new AiReplyApi(api);

  return useMutation({
    mutationFn: (body: AiReplyRequestBody) => aiReplyApi.postV2(body),
    mutationKey: ['ai-reply', 'v2'],
  });
};

export const useAiReplyGPT = () => {
  const api = new AppApi(baseUrl);
  const aiReplyApi = new AiReplyApi(api);

  return useMutation({
    mutationFn: (body: AiReplyRequestBody) => aiReplyApi.postGPT(body),
    mutationKey: ['ai-reply', 'gpt'],
  });
};

// /ai-replies/v1/multiple, /ai-replies/samples 는 로그인 필요 (BE #1822)
export const useAiRepliesMultiple = (rating: number) => {
  const { accessToken } = useAuth();

  const getAiRepliesMultiple = async () => {
    const api = new AppApi(baseUrl);
    api.setup(accessToken);
    const aiRepliesApi = new AiReplyApi(api);

    return aiRepliesApi.getAiRepliesMultiple(rating);
  };

  return useQuery({
    queryKey: cleanArray(['ai-replies-multiple', rating]),
    queryFn: getAiRepliesMultiple,
    enabled: !!accessToken,
  });
};

export const useSampleReply = (shopIdOrIds: string | string[]) => {
  const { accessToken } = useAuth();
  // 매장 별명 설정 미리보기는 단일 id 로 호출함
  const shopIds = Array.isArray(shopIdOrIds) ? shopIdOrIds : [shopIdOrIds];

  const getAiReplyWithShopIds = async () => {
    const api = new AppApi(baseUrl);
    api.setup(accessToken);
    const aiRepliesApi = new AiReplyApi(api);

    return aiRepliesApi.getSampleAiReplyWithShopIds(shopIds);
  };

  return useQuery({
    queryKey: cleanArray(['sample-reply', shopIds.join(',')]),
    queryFn: getAiReplyWithShopIds,
    enabled: !!accessToken && shopIds.length > 0,
  });
};
